import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { BookingOptions, VenueConfig } from "../../../common/jsonRpcTypes";
import type { BookingStage, PendingBooking, PreorderSelections } from "./BookingForm";
import PaymentProcessorSwitch from "../shared/PaymentProcessorSwitch";
import { canUseStripeConnect, extractPaymentDetails } from "../../utils/paymentProcessorUtils";
import PrimaryButton from "../buttons/primaryButton";
import { CTA_FULL_WIDTH } from "../buttons/ctaClassNames";
import VoucherRedemption from "./VoucherRedemption";
import PaymentSummary from "./PaymentSummary";
import AlertBanner from "../shared/AlertBanner";
import SlowRequestNotice from "../shared/SlowRequestNotice";
import CreditCardDisabledNotice from "../shared/CreditCardDisabledNotice";
import { useVoucherRedemption, type AppliedVoucher } from "../hooks/useVoucherRedemption";
import { loadVouchersFromUrl } from "../../utils/voucherUrlUtils";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { usePaymentCalculations } from "../hooks/usePaymentCalculations";
import { useTaxConfiguration } from "../hooks/useTaxConfiguration";
import { useBookingData } from "../hooks/useBookingData";
import { usePaymentDisplay } from "../hooks/usePaymentDisplay";
import { isCreditCardPaymentDisabled } from "../../utils/paymentHelpers";
import { useInFlight } from "../hooks/useInFlight";
import { dietaryRequirementsToCustomerNotes } from "../preOrders/preOrderUtils";
import { withoutDietaryNotes } from "../../../common/dietaryRequirements";

interface PaymentStepProps {
  readonly bookingOptions: BookingOptions | null;
  readonly venueConfig: VenueConfig | null;
  readonly stage: BookingStage;
  readonly pendingBookingId: string | null;
  readonly booking: PendingBooking | null;
  readonly preorderSelections: PreorderSelections | null;
  readonly paymentProcessor: string;
  readonly countryCode: string;
  readonly customerName?: string;
  readonly customerEmail?: string;
  readonly onBack: () => void;
  readonly onPaymentComplete: (paymentDetails?: unknown) => void | Promise<void>;
  /** Notified when the applied voucher list changes so codes reach submission. */
  readonly onVouchersChange?: (vouchers: AppliedVoucher[]) => void;
  readonly headingSlot?: HTMLElement | null;
  /**
   * Hides the "Complete booking" CTA and the card payment form — set when the
   * booking's outcome is already unknown (a timed-out `/submitBooking` or payment).
   */
  readonly submitHidden?: boolean;
  /** Called when a payment request times out, so whether it went through is unknown. */
  readonly onPaymentUnconfirmed?: () => void;
}

/**
 * PaymentStep — Step 6 of the booking journey.
 *
 * Collects card payment for the booking deposit and/or pre-order.
 * Uses the existing PaymentProcessorSwitch to render either
 * StripeConnect or FreedomPay based on the venue's payment processor.
 *
 * Supports voucher input when the venue group has Soda integration.
 *
 * Business logic (amount calculation, payment initialisation,
 * processor selection) is intentionally omitted.
 */
const PaymentStep: React.FC<PaymentStepProps> = ({
  bookingOptions,
  venueConfig,
  stage,
  pendingBookingId,
  booking,
  preorderSelections,
  paymentProcessor,
  countryCode,
  customerName,
  customerEmail,
  onBack,
  onPaymentComplete,
  onVouchersChange,
  headingSlot,
  submitHidden = false,
  onPaymentUnconfirmed,
}) => {
  const { t } = useTranslation();
  const completeGuard = useInFlight();

  const depositRequired = bookingOptions?.depositRequired;
  const isDepositOnly = stage === "deposit";
  const isAuthOnly = depositRequired?.type === "auth" || depositRequired?.type === "authenticate";
  const currency = venueConfig?.currency ?? "GBP";

  // Venue-authored deposit terms. Blank or whitespace-only text must not render
  // an empty alert. Rendered as escaped plain text — never as markup.
  const depositTerms = depositRequired?.terms?.trim();

  // The pre-order payment is bundled with a deposit or authentication, so the API
  // reports its standalone required payment as 0. When the pre-order must still be
  // paid by the lead booker, the full pre-order total is due — reflect that in both
  // the amount calculation and the summary breakdown. Genuine partial payments
  // (required payment already > 0) and pay-at-venue pre-orders are left untouched.
  const effectivePreorderSelections = useMemo<PreorderSelections | null>(() => {
    if (
      !preorderSelections ||
      preorderSelections.total <= 0 ||
      preorderSelections.requiredPaymentAmount > 0 ||
      !preorderSelections.preorderPaymentRequired
    ) {
      return preorderSelections;
    }
    return {
      ...preorderSelections,
      requiredPaymentAmount: preorderSelections.total,
      requiredPaymentGross:
        preorderSelections.requiredPaymentGross && preorderSelections.requiredPaymentGross > 0
          ? preorderSelections.requiredPaymentGross
          : preorderSelections.totalGross,
    };
  }, [preorderSelections]);

  // Stripe Connect creates the booking from a webhook, so the order note and
  // dietary requirements travel with the checkout. They are only saved with a
  // pre-order that has items, and the note is sent once, as customer_notes.
  const preorderGuestDetails = useMemo(() => {
    if (!preorderSelections || (preorderSelections.rawItems?.length ?? 0) === 0) return {};
    return {
      customerNotes: dietaryRequirementsToCustomerNotes(preorderSelections.dietaryRequirements),
      dietaryRequirements: withoutDietaryNotes(preorderSelections.dietaryRequirements ?? []),
    };
  }, [preorderSelections]);

  // Helper function to check if there are no minimum preorder rules
  const hasNoPreorderRules = useMemo(() => {
    if (!bookingOptions?.preorders || !preorderSelections) return true;

    const preorderMenus = bookingOptions.preorders[booking?.type?.id ?? ""] ?? [];
    if (preorderMenus.length === 0) return true;

    // Check if any menu has minimum spend requirements
    const hasMinimumSpendRequirements = preorderMenus.some((menu) => (menu.minimum_preorder_amount ?? 0) > 0);

    // Check if any menu has item limit requirements
    const hasItemLimitRequirements = preorderMenus.some((menu) => {
      return (
        menu.limit_preorders &&
        ((menu.limit_preorders.min && menu.limit_preorders.min > 0) ||
          (menu.limit_preorders.max && menu.limit_preorders.max > 0))
      );
    });

    return !hasMinimumSpendRequirements && !hasItemLimitRequirements;
  }, [bookingOptions, preorderSelections, booking?.type?.id]);

  // Whether the pre-order actually has an amount due now. A pre-order with items
  // but no required payment (pay-at-venue) is not "payable" — the auth/authenticate
  // banner and payment summary visibility below must key off this, not merely
  // whether items were added.
  const isPreorderPayable = !!(effectivePreorderSelections && effectivePreorderSelections.requiredPaymentAmount > 0);

  // Use extracted hooks to reduce cognitive complexity
  const { isDepositTaxExclusive, isPreorderTaxExclusive, isEffectiveTaxExclusive, effectiveTaxLabel } =
    useTaxConfiguration(depositRequired, effectivePreorderSelections, isAuthOnly);

  const paymentAmount = usePaymentCalculations(
    depositRequired,
    effectivePreorderSelections,
    isAuthOnly,
    isDepositOnly,
    isDepositTaxExclusive,
    isPreorderTaxExclusive,
  );

  // Voucher redemption is offered only for Soda venues taking an actual payment
  // (never for auth-only deposits), mirroring DMN's prepareSodaVoucherRedemption.
  const showVoucherInput = (venueConfig?.venueGroupHasSodaIntegration ?? false) && !isAuthOnly && paymentAmount > 0;

  // Load vouchers from URL parameters to restore after Stripe Connect redirect
  const initialVouchers = useMemo(() => {
    return showVoucherInput ? loadVouchersFromUrl() : [];
  }, [showVoucherInput]);

  const {
    appliedVouchers,
    loading: voucherLoading,
    error: voucherError,
    apply,
    remove,
    redeemedTotal,
    remainingAmount,
  } = useVoucherRedemption({
    date: booking?.date ?? "",
    venue: venueConfig?.venueId ?? "",
    baseAmount: paymentAmount,
    onVouchersChange,
    initialVouchers,
  });

  // Use extracted hook for display-related calculations
  const {
    getAmountToPayOnCardWithoutVouchers,
    getAmountToPayOnCardWithVouchers,
    getIsFullyRedeemedWithoutVouchers,
    getIsFullyRedeemedWithVouchers,
  } = usePaymentDisplay(venueConfig, isAuthOnly, paymentAmount, appliedVouchers, remainingAmount);

  // Select appropriate methods based on voucher input visibility
  const amountToPayOnCard = showVoucherInput ? getAmountToPayOnCardWithVouchers : getAmountToPayOnCardWithoutVouchers;
  const isFullyRedeemed = showVoucherInput ? getIsFullyRedeemedWithVouchers : getIsFullyRedeemedWithoutVouchers;

  const useStripe = canUseStripeConnect({ paymentProcessor, venue: venueConfig });

  // Collins refuses credit-funded cards for this booking type, so warn the guest
  // before they enter card details and let the payment form refuse the card
  // before it is charged.
  const creditCardsDisabled = isCreditCardPaymentDisabled(venueConfig, booking?.type?.id);

  const bookingData = useBookingData(
    {
      booking,
      pendingBookingId,
    },
    {
      venueConfig,
      currency,
    },
    {
      depositRequired,
      paymentProcessor,
      isAuthOnly,
      isDepositTaxExclusive,
    },
  );

  const heading = (
    <h2 id="payment-heading" className="gcw:text-2xl gcw:font-heading gcw:font-bold gcw:text-text-primary gcw:mb-bs-3">
      {isDepositOnly ? t("booking_deposit_title") : t("booking_payment_title")}
    </h2>
  );

  return (
    <section id="gc-payment-step" aria-labelledby="payment-heading" className="gcw:lg:p-6">
      {headingSlot ? createPortal(heading, headingSlot) : heading}

      {/* Card validation message for authenticate deposits when the pre-order (if any) isn't being paid now */}
      {isAuthOnly && !isPreorderPayable && (
        <AlertBanner
          variant="info"
          translatedKey={
            hasNoPreorderRules ? "booking_card_validation_auth_deposit_message" : "booking_card_validation_message"
          }
          messageDefault={
            hasNoPreorderRules
              ? "Your card will be authenticated for {{amount}} to confirm your booking."
              : "To confirm your booking at {{venue}}, we need to authenticate your card for {{amount}}. No money will be taken at this time."
          }
          translationParams={{
            venue: venueConfig?.title ?? "",
            amount: formatCurrency(depositRequired?.total ?? 0, currency, countryCode),
          }}
          className="gcw:mb-bs-3"
        />
      )}

      {/* Payment summary - shown when the preorder has an amount due in payment stage, or for non-auth deposits */}
      {(!isAuthOnly || (stage === "payment" && isPreorderPayable)) && (
        <PaymentSummary
          isDepositOnly={isDepositOnly}
          isAuthOnly={isAuthOnly}
          depositRequired={depositRequired}
          isDepositTaxExclusive={isDepositTaxExclusive}
          preorderSelections={effectivePreorderSelections}
          isPreorderTaxExclusive={isPreorderTaxExclusive}
          paymentAmount={paymentAmount}
          isEffectiveTaxExclusive={isEffectiveTaxExclusive}
          effectiveTaxLabel={effectiveTaxLabel}
          showVoucherInput={showVoucherInput}
          redeemedTotal={redeemedTotal}
          amountToPayOnCard={amountToPayOnCard}
          currency={currency}
          countryCode={countryCode}
          preorderTotal={effectivePreorderSelections?.total}
          preorderRequiredPayment={effectivePreorderSelections?.requiredPaymentAmount}
        />
      )}

      {/* Venue's deposit terms — shown for every deposit type when the venue supplies text */}
      {depositTerms && (
        <AlertBanner
          variant="info"
          className="gcw:mb-bs-3"
          textClassName="gcw:whitespace-pre-line gcw:text-body gcw:text-text-secondary"
        >
          {depositTerms}
        </AlertBanner>
      )}

      {/* Voucher input */}
      {showVoucherInput && (
        <VoucherRedemption
          appliedVouchers={appliedVouchers}
          loading={voucherLoading}
          error={voucherError}
          currency={currency}
          countryCode={countryCode}
          onApply={apply}
          onRemove={remove}
        />
      )}

      {/* Payment processor — or a direct completion when vouchers cover the full amount */}
      {isFullyRedeemed ? (
        <div className="gcw:rounded-md gcw:border gcw:border-gray-200 gcw:bg-bg-surface gcw:p-bs-3 gcw:mb-bs-3">
          <p className="gcw:text-body gcw:text-text-secondary gcw:mb-bs-3">
            {t(
              "booking_voucher_fully_redeemed_note",
              "Your vouchers cover the full amount, so there is nothing to pay on card.",
            )}
          </p>
          <SlowRequestNotice />
          {!submitHidden && (
            <PrimaryButton
              text={t("booking_voucher_complete", "Complete booking")}
              type="button"
              onClick={() => void completeGuard.run(async () => onPaymentComplete())}
              loading={completeGuard.isPending}
              className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 ${CTA_FULL_WIDTH}`}
              ariaLabel={t("booking_voucher_complete", "Complete booking")}
            />
          )}
        </div>
      ) : (
        /* When the payment outcome is already unknown the processor must not be
           offered again — submitting it could double-charge the guest. */
        !submitHidden && (
          <fieldset
            id="gc-payment-processor"
            className="gcw:m-0 gcw:min-w-0 gcw:border-0 gcw:p-0"
            aria-label={t("booking_payment_processor_area")}
          >
            {creditCardsDisabled && <CreditCardDisabledNotice />}
            <SlowRequestNotice />
            <PaymentProcessorSwitch
              useStripeConnect={useStripe}
              stripeProps={{
                pendingBookingId: pendingBookingId ?? undefined,
                amount: amountToPayOnCard,
                bookingData,
                showInfo: false,
                action: "book",
                name: customerName,
                email: customerEmail,
                ...preorderGuestDetails,
                mode: "full",
                vouchers: appliedVouchers,
                disableCreditCards: creditCardsDisabled,
              }}
              freedomPayProps={{
                pendingBookingId: pendingBookingId ?? undefined,
                amount: amountToPayOnCard,
                bookingData,
                showInfo: false,
                action: "book",
                name: customerName,
                email: customerEmail,
                mode: "full",
                vouchers: appliedVouchers,
                onPaymentSuccess: (_email, _amount, paymentDetails) =>
                  onPaymentComplete(extractPaymentDetails(paymentDetails)),
                onPaymentUnconfirmed,
              }}
            />
          </fieldset>
        )
      )}
    </section>
  );
};

export default PaymentStep;
